import Markdown from "react-markdown"

import { ContactLookupArtifact } from "@/components/ask/artifacts/contact-lookup-artifact"
import { FieldsDisplayArtifact } from "@/components/ask/artifacts/fields-display-artifact"
import { AttachmentChip } from "@/components/ask/attachment-chip"
import type { ChatMessage } from "@/types"

interface MessageBubbleProps {
  message: ChatMessage
  onAction?: (message: string) => void
}

export function MessageBubble({ message, onAction }: MessageBubbleProps) {
  const isAssistant = message.role === "assistant"

  if (isAssistant) {
    return (
      <div>
        <div className="prose dark:prose-invert max-w-none text-[15px]">
          <Markdown
            components={{
              code({ className, children, ...props }) {
                const match = className?.match(/language-artifact:(.+)/)
                const artifactType = match?.[1]
                const text = typeof children === "string" ? children : ""

                if (artifactType === "fields_display") {
                  return (
                    <FieldsDisplayArtifact data={text} onAction={onAction} />
                  )
                }
                if (artifactType === "contact_lookup") {
                  return (
                    <ContactLookupArtifact data={text} onAction={onAction} />
                  )
                }
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                )
              },
              pre({ children }) {
                return <>{children}</>
              },
            }}
          >
            {message.content ?? ""}
          </Markdown>
        </div>
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {message.attachments.map((attachment) => (
              <AttachmentChip
                key={attachment.filename}
                filename={attachment.filename}
                fileSize={attachment.byte_size}
                contentType={attachment.content_type}
                href={attachment.url}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-end gap-2">
      {message.attachments && message.attachments.length > 0 && (
        <div className="flex flex-col items-end gap-2">
          {message.attachments.map((attachment) => (
            <AttachmentChip
              key={attachment.filename}
              filename={attachment.filename}
              fileSize={attachment.byte_size}
              contentType={attachment.content_type}
              href={attachment.url}
            />
          ))}
        </div>
      )}
      {message.content && (
        <div className="bg-muted max-w-4/5 rounded-2xl px-4 py-2.5">
          {/* IMP-362: preserve newlines so the structured confirmation-card
              submission messages render as a list, not one run-on paragraph. */}
          <div className="text-[15px] break-words whitespace-pre-wrap">
            {message.content}
          </div>
        </div>
      )}
    </div>
  )
}
